import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trophy, Lock, PushPin, Star, Sparkle } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

const RARITY = {
  Common: "text-ff-muted border-ff-muted/30",
  Uncommon: "text-ff-success border-ff-success/40",
  Rare: "text-sky-400 border-sky-400/40",
  Epic: "text-fuchsia-400 border-fuchsia-400/40",
  Legendary: "text-ff-gold border-ff-gold/50",
};

export default function Achievements() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("All");

  const load = () => api.get("/achievements").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="py-20 text-center text-ff-muted">Opening the hall of legends...</div>;

  const cats = ["All", ...Array.from(new Set(data.achievements.map((a) => a.category)))];
  const list = data.achievements.filter((a) => filter === "All" || a.category === filter)
    .sort((a, b) => (b.unlocked - a.unlocked) || (b.priority - a.priority));

  const togglePin = async (a) => {
    if (!a.unlocked) return;
    let pins = data.pinned.includes(a.id) ? data.pinned.filter((x) => x !== a.id) : [...data.pinned, a.id];
    if (pins.length > 3) return toast.error("Pin at most three achievements");
    try {
      await api.post("/achievements/pin", { achievement_ids: pins });
      toast.success("Pinned achievements updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="achievements-page">
      <div className="mb-1 flex items-center gap-2">
        <Trophy size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-4xl font-bold tracking-tight">Achievements</h1>
      </div>
      <div className="mb-5 flex items-center gap-4 text-sm text-ff-muted">
        <span data-testid="ach-count">{data.unlocked_count}/{data.total} unlocked</span>
        <div className="h-2 w-40 overflow-hidden rounded-full bg-ff-bg">
          <div className="h-full bg-ff-gold" style={{ width: `${data.completion}%` }} />
        </div>
        <span className="font-mono-ff text-ff-gold">{data.completion}%</span>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {cats.map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`ach-filter-${c}`}
            className={`rounded-sm border px-3 py-1 text-xs transition-colors ${filter === c ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/30 text-ff-muted hover:text-ff-parchment"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
            className={`ff-panel relative rounded-md p-4 ${a.unlocked ? "" : "opacity-70"}`} data-testid={`achievement-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {a.unlocked ? <Sparkle size={20} weight="duotone" className="text-ff-gold" /> : a.hidden ? <Lock size={18} className="text-ff-muted" /> : <Star size={18} className="text-ff-muted" />}
                <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] ${RARITY[a.rarity] || ""}`}>{a.rarity}</span>
              </div>
              {a.unlocked && (
                <button onClick={() => togglePin(a)} data-testid={`pin-${a.id}`} title="Pin to profile">
                  <PushPin size={16} weight={a.pinned ? "fill" : "regular"} className={a.pinned ? "text-ff-gold" : "text-ff-muted hover:text-ff-gold"} />
                </button>
              )}
            </div>
            <h3 className={`mt-2 font-display text-lg font-semibold ${a.unlocked ? "text-ff-parchment" : "text-ff-muted"}`}>{a.name}</h3>
            <p className="text-xs text-ff-muted">{a.description}</p>
            <div className="mt-2 text-[10px] uppercase tracking-wide text-ff-brass">{a.category}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
